import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { NavDto } from "./NavDto";
import { ALL_NAV_ITEMS } from "./nav-item-register";
import { Star } from "lucide-angular";
import { Router } from "@angular/router";
import { AccessControlService } from "../../../../services/accesscontrol.service";
import { SignalRService } from "../../../../services/signalr.service";
import { SessionService } from "../../../../services/session.service";

@Injectable({ providedIn: "root" })
export class NavService {
  public urlUndefined = "none";
  // UI-Konfiguration
  accessControlService = inject(AccessControlService);
  mutation = this.accessControlService.hasPermissionsMutation();
  signalrService = inject(SignalRService);
  sessionService = inject(SessionService);

  timeMachineEnabled = false;
  private _items = signal<NavDto[]>([]);
  readonly items = computed(() => this._items());
  readonly leafItems = computed(() => {
    function getLeafs(item: NavDto): NavDto[] {
      if (!item.children || item.children.length === 0) {
        return [item];
      }

      return item.children.flatMap(getLeafs);
    }

    return this.items().flatMap(getLeafs);
  });
  /** Bottom Items */
  readonly bottomItems = computed(() => {
    const items = this.items().filter((i) => i.location === "aside-fixed-b");
    // const fav = this.favouritesItem();
    // if (!!fav) {
    //   items.push(fav);
    // }
    return items;
  });
  private _favoriteRoutes = signal<string[]>([]);
  /** Favoriten (vereinfachte Demo-Logik) */
  readonly favorites = computed(() =>
    this.items().filter((i) => i.route && this._favoriteRoutes().includes(i.route())),
  );

  constructor() {
    const all = [...ALL_NAV_ITEMS()];
    //this.init(all);

    effect(() => {
      this.timeMachineEnabled = this.sessionService.getSessionInfo()()?.timeMachineEnabled ?? false;
      this.init(all);
    });

    this.signalrService.permissionChanged$.subscribe((value) => {
      setTimeout(() => {
        this.init(all);
      }, 500); // Kurze Verzögerung, damit evtl. Session-Updates abgeschlossen sind
    });
  }

  init(all: NavDto[]) {
    this.accessControlService
      .filterPermissions(all, (x: NavDto) => x.permission!, this.mutation)
      .then((filteredItems) => {
        if (!this.timeMachineEnabled) {
          filteredItems = filteredItems.filter((x) => x.name !== "Zeitmaschine");
        }

        // Infobox visibility: BMSGPK + (Freigeber OR Bearbeiter)
        return this.filterInfoboxVisibility(filteredItems);
      })
      .then((filteredItems) => {
        return this._items.set(filteredItems);
      });
  }

  private async filterInfoboxVisibility(items: NavDto[]): Promise<NavDto[]> {
    const result = await this.mutation.mutateAsync(["bMSGPKDisplay", "freigeberDisplay", "bearbeiterDisplay"]);
    const hasBMSGPK = result["bMSGPKDisplay"] === true;
    const hasFreigeber = result["freigeberDisplay"] === true;
    const hasBearbeiter = result["bearbeiterDisplay"] === true;

    const shouldShow = hasBMSGPK && (hasFreigeber || hasBearbeiter);

    return items.map((item) => {
      if (item.name === "Infobox") {
        return shouldShow ? item : undefined;
      }
      return item;
    }).filter((item): item is NavDto => item !== undefined);
  }

  // UI-Open-States

  openMenusForActiveRoute(router: Router) {
    const normalizeRoute = this.normalizeRoute;
    const currentUrl = normalizeRoute(router.url.split("?")[0]);

    function findRouteMatchInChildren(navItem: NavDto): boolean {
      navItem.isOpen.set(false);
      navItem.isActive = false;

      if (navItem.route) {
        const split_route: string[] = navItem.route().split("/");
        const subrouteMatch = currentUrl.startsWith(split_route.join("/"));
        const routeMatch = normalizeRoute(navItem.route()) === currentUrl;

        if (routeMatch || subrouteMatch) {
          navItem.isActive = true;
          return true;
        }
      }
      const found = navItem.children.some(findRouteMatchInChildren);
      if (found) {
        navItem.isOpen.set(true);
      }
      return found;
    }

    this.items().forEach((item) => {
      findRouteMatchInChildren(item);
    });
  }

  normalizeRoute(path: string): string {
    return path.replace(/^\/+/, "").replace(/\/+$/, "");
  }

  globalItems(): NavDto[] {
    const items = this.items().filter((i) => i.location === "aside");
    return items;
  }

  favouritesItem() {
    if (this.favorites().length !== 0) {
      return new NavDto({
        name: "Favoriten",
        icon: Star,
        location: "aside-fixed-b",
        children: this.favorites(),
      });
    }
    return undefined;
  }

  // Favoriten-Logik
  toggleFavorite(route: string) {
    const favs = [...this._favoriteRoutes()];
    const idx = favs.indexOf(route);
    if (idx >= 0) favs.splice(idx, 1);
    else favs.push(route);
    this._favoriteRoutes.set(favs);
  }
}
